import store from '../stores/main';
import { ROOM_STATES, SettingsReducerState } from '../stores/reducers/settings';
import { Action, GreenroomState } from '../stores/types/types';
import {
	SyncedActionData, SyncedActionMessage, syncedReduxActions, SyncedState,
} from './sync-constants';
import { SYNC_GRAPHICS_REDUCER_STATE } from '../stores/actions/graphics';
import { SYNC_BROADCAST_REDUCER_STATE } from '../stores/actions/broadcasting';
import { SET_CANVAS_VOLUME_BACKSTAGE } from '../stores/actions/meeting';
import { LOCATION_CANVAS_ROUTES } from '../stores/reducers/video';
import { PresentedMediaItem } from '../stores/reducers/media';

/* this is sent to the remote server when we are retrieving state. It's
neccisary to update each piece of state individually to prevent overwrites,
so we have to know what pieces (properties) we're looking for when we read */
export const getSyncedPropsArray = (): string[] => {
	const syncedProperties: string[] = [];

	syncedReduxActions.forEach((syncedAction: SyncedActionData) => {
		syncedProperties.push(syncedAction.stateVar);
	});

	return syncedProperties;
};

/* some actions we do are conditionally synced - meaning sync can be gated by other areas of the app
for example, we don't want current shot to be synced when the room is in the showbuilder state */
const handleConditionalState = async (newSyncedState: SyncedState) => {
	const oldMainState = store.getState();
	const returnedSyncState = newSyncedState;

	if (returnedSyncState.shotsReducer) {
		if (oldMainState.MeetingReducer.roomState === ROOM_STATES.EDITING) {
			// if room state is editing, the shot should not be synced
			returnedSyncState.shotsReducer.currentShotId = oldMainState.ShotsReducer.currentShotId;
		}
	}

	return returnedSyncState;
};

/* this function takes the returned value from the remote, and applies all the synced variables
across the reducers. If you need to run any logic based on requested synced state, this is where
you would do that. But please, try and keep your logic inexpensive, we want this to be a fast
function, so people aren't waiting to join the room for a long time.

If you are adding a reducer that doesn't yet have a SYNC_*reducer*_REDUCER_STATE action, look in
either settings or shots, and copy that functionality over to the new reducer */
export const parseAndApplyStateFromRemote = async (newSyncedState: SyncedState) => {
	const stateToApply = await handleConditionalState(newSyncedState);

	if (stateToApply.shotsReducer) {
		store.dispatch({ type: 'SYNC_SHOTS_REDUCER_STATE', payload: stateToApply.shotsReducer });
	}

	if (stateToApply.settingsReducer) {
		store.dispatch({ type: 'SYNC_SETTINGS_REDUCER_STATE', payload: stateToApply.settingsReducer });
	}

	if (stateToApply.mediaReducer) {
		store.dispatch({ type: 'SYNC_MEDIA_REDUCER_STATE', payload: stateToApply.mediaReducer });
	}

	if (stateToApply.graphicsReducer) {
		store.dispatch({ type: SYNC_GRAPHICS_REDUCER_STATE, payload: stateToApply.graphicsReducer });
	}

	if (stateToApply.broadcastReducer) {
		store.dispatch({ type: SYNC_BROADCAST_REDUCER_STATE, payload: stateToApply.broadcastReducer });
	}

	if (stateToApply.meetingReducer) {
		const { backstageCanvasVolume } = stateToApply.meetingReducer;
		if (backstageCanvasVolume === 0 || backstageCanvasVolume) {
			store.dispatch(
				{
					type: SET_CANVAS_VOLUME_BACKSTAGE,
					payload: Number(stateToApply.meetingReducer.backstageCanvasVolume),
				},
			);
		}
	}

	store.dispatch({ type: 'SET_HAS_SYNCED', payload: true });
};

/* for the socket listener to parse conditional actions */
export const handleConditionalAction = async (message: SyncedActionMessage) => {
	const oldMainState = store.getState() as GreenroomState;
	const returnedAction = message.action as Action;

	/* if data is conditional, it has been set that way in syncedReduxActions map,
	an indication the client app's state will affect this action */
	if (message.data.conditional) {
		if (returnedAction.type === 'SET_CURRENT_SHOT_ID') {
			// we don't sync the current shot if we're in the editing room state
			if (oldMainState?.MeetingReducer?.roomState === ROOM_STATES.EDITING) {
				return null;
			}
		}
		if (returnedAction.type === 'SET_VIDEO_TITLES_HAVE_FIRED') {
			// we don't sync this action, it only fires to beehive to mutate the v-1 sync store
			return null;
		}
		if (returnedAction.type === 'SET_PRESENTED_MEDIA') {
			const {
				VideoReducer: {
					onsiteLocationSession,
					onsiteLocations,
				},
				MediaReducer: {
					recentSlideDeckEntity,
					presentedMedia,
				},
			} = oldMainState;

			const locationData = onsiteLocations.find((ls) => ls.id === onsiteLocationSession?.id);
			if (
				onsiteLocationSession
				&& LOCATION_CANVAS_ROUTES.includes(onsiteLocationSession.route)
				&& locationData?.useSlideDeck
				&& recentSlideDeckEntity
			) {
				const oldPresentedItem = presentedMedia.find(
					(m) => m.mediaId === recentSlideDeckEntity.entityValue.mediaId,
				);
				const newPresentedItem = returnedAction.payload.find(
					(m: PresentedMediaItem) => m.mediaId === recentSlideDeckEntity.entityValue.mediaId,
				);

				returnedAction.payload = returnedAction.payload.map((item: PresentedMediaItem) => {
					if (
						item.mediaId === recentSlideDeckEntity.entityValue.mediaId
						&& oldPresentedItem?.videoState
						&& !newPresentedItem.videoState
					) {
						return {
							...item,
							videoState: oldPresentedItem.videoState,
						};
					}
					return item;
				});
			}
		}
	}

	return returnedAction;
};

export const replaceSettingsJSONPropsUsingSyncedState = (settings: SettingsReducerState) => {
	const syncedProps = getSyncedPropsArray();
	const currentSettings = store.getState().SettingsReducer;

	// if the settings reducer has not been synced via Beehive, use the saved props from settings_json
	if (!currentSettings.hasSettingsFromBeehive) {
		return settings;
	}

	// eslint-disable-next-line no-restricted-syntax
	for (const prop of syncedProps) {
		// @ts-ignore typescript hates this syntax
		// eslint-disable-next-line no-param-reassign
		settings[prop] = currentSettings[prop];
	}

	return settings;
};

export const getStateFromSettingsJson = (
	settingsJsonString: string,
): SettingsReducerState => {
	const settingsJson = JSON.parse(settingsJsonString);

	settingsJson.hasSynced = true;
	/*
		`settingsJson.featureFlags`:
		Exists for some older meetings in the DB before a code change
		that stopped saving featureFlags to the meeting settings_json.
	*/
	delete settingsJson.featureFlags;

	// This has been moved to beehive, but may exist on settings_json for older meetings
	delete settingsJson.roomState;

	return replaceSettingsJSONPropsUsingSyncedState(settingsJson);
};
